import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, fetchActivePolicy, fetchMe, fetchWorkLocations } from './api/client';
import type { AuthState } from './api/request';
import type { AttendancePolicyResponse, MeResponse, WorkLocationResponse } from './api/types';
import { WorkforceContext, type WorkforceSession } from './app/WorkforceContext';
import { ErrorCard, LoadingState } from './states';

interface Props {
  readonly auth: AuthState | null;
  readonly onSignOut: () => void;
  readonly children: (session: WorkforceSession) => ReactNode;
}

/**
 * AuthBoundary — batas autentikasi di sisi klien, sekaligus pemuat konteks sesi.
 *
 * Ia menentukan apa yang DITAMPILKAN, bukan apa yang DIIZINKAN. Penegakan izin tetap
 * sepenuhnya di API; boundary ini hanya mencegah layar kosong dan menu yang menyesatkan.
 * Menghapus komponen ini tidak membuat seorang pun memperoleh akses tambahan.
 *
 * Kebijakan absensi dan lokasi kerja dimuat di sini, sekali, karena hampir seluruh perilaku
 * layar check-in bergantung padanya. `503` dari layanan keanggotaan sengaja TIDAK diperhalus:
 * server memilih 503 justru karena ia tidak tahu status keanggotaan, dan menampilkan data lama
 * seolah normal akan menyembunyikan ketidaktahuan itu.
 */
export function AuthBoundary({ auth, onSignOut, children }: Props): ReactNode {
  const [state, setState] = useState<
    | { status: 'idle' }
    | { status: 'loading' }
    | {
        status: 'ready';
        me: MeResponse;
        policy: AttendancePolicyResponse | null;
        workLocations: readonly WorkLocationResponse[];
      }
    | { status: 'error'; error: ApiError }
  >({ status: 'idle' });

  const [reloadToken, setReloadToken] = useState(0);
  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  useEffect(() => {
    if (!auth) {
      setState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      const me = await fetchMe(auth);
      // Kebijakan & lokasi kerja bersifat pelengkap: ketiadaannya BUKAN kegagalan sesi.
      // Organisasi yang belum menyiapkan kebijakan tetap harus bisa masuk dan melihat
      // penjelasan, bukan terkunci di layar galat.
      const [policy, workLocations] = await Promise.all([
        fetchActivePolicy(auth).catch(() => null),
        fetchWorkLocations(auth).catch(() => [] as readonly WorkLocationResponse[]),
      ]);
      return { me, policy, workLocations };
    })()
      .then(({ me, policy, workLocations }) => {
        if (!cancelled) setState({ status: 'ready', me, policy, workLocations });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          error:
            error instanceof ApiError
              ? error
              : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [auth, reloadToken]);

  if (!auth || state.status === 'idle') {
    return <SignInPrompt />;
  }
  if (state.status === 'loading') {
    return <LoadingState label="Memeriksa keanggotaan organisasi…" />;
  }
  if (state.status === 'error') {
    return <ErrorCard error={state.error} onRetry={reload} onSignOut={onSignOut} />;
  }

  const session: WorkforceSession = {
    auth,
    me: state.me,
    policy: state.policy,
    workLocations: state.workLocations,
    reload,
    onSignOut,
  };

  return <WorkforceContext.Provider value={session}>{children(session)}</WorkforceContext.Provider>;
}

function SignInPrompt(): ReactNode {
  return (
    <section className="card">
      <h2>Masuk diperlukan</h2>
      <p>
        SalesHub Pro Workforce memakai akun SalesHub Pro Anda. Silakan masuk lewat halaman login
        untuk melanjutkan.
      </p>
    </section>
  );
}
